import React from 'react';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import { injectIntl, FormattedMessage } from 'react-intl';
import cx from 'classnames';
import { Form, Col, Row, Container, Button } from 'react-bootstrap';
import { GoogleMap, Polygon, OverlayView, useJsApiLoader } from '@react-google-maps/api';

import s from './LocationForm.css';
import messages from '../../../../locale/messages';
import Link from '../../../Link';
import validate from './validate';
import { manageLocation } from '../../../../actions/siteadmin/Location/manageLocation';
import { googleMapAPI } from '../../../../config';

const mapContainerStyle = { height: '400px', width: '100%' };
const DEFAULT_CENTER = { lat: -6.2, lng: 106.816666 };
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

function GoogleMapsLoader({ children }) {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: googleMapAPI,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    if (!isLoaded) return <div>Loading map...</div>;
    return children;
}

class LocationFormComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            polygonPath: [],
            mapCenter: DEFAULT_CENTER,
        };
        this.handleMapClick = this.handleMapClick.bind(this);
        this.handleClearPolygon = this.handleClearPolygon.bind(this);
    }

    componentDidMount() {
        const coordinates = this.props.initialValues?.coordinates;
        if (coordinates) {
            try {
                const path = JSON.parse(coordinates);
                if (Array.isArray(path) && path.length > 0) {
                    this.setState({ polygonPath: path, mapCenter: path[0] });
                }
            } catch (_) {
                // invalid JSON, ignore
            }
        }
    }

    handleMapClick(event) {
        const newPoint = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        this.setState(prev => {
            const path = [...prev.polygonPath, newPoint];
            this.props.change('coordinates', JSON.stringify(path));
            return { polygonPath: path };
        });
    }

    handleClearPolygon() {
        this.setState({ polygonPath: [] });
        this.props.change('coordinates', '');
    }

    renderField = ({ input, label, type, meta: { touched, error }, placeholder }) => {
        const { formatMessage } = this.props.intl;
        return (
            <Form.Group className={cx(s.space5, 'positionRelative')}>
                <Form.Label className={'inputLabel'}>{label}</Form.Label>
                <Form.Control {...input} placeholder={placeholder || label} type={type} className={s.formControlInput} />
                {touched && error && (
                    <span className={cx(s.errorMessage, 'errorMessageRTL')}>{formatMessage(error)}</span>
                )}
            </Form.Group>
        );
    }

    renderTextArea = ({ input, label, meta: { touched, error } }) => {
        const { formatMessage } = this.props.intl;
        return (
            <Form.Group className={cx(s.space5, 'positionRelative')}>
                <Form.Label className={'inputLabel'}>{label}</Form.Label>
                <Form.Control {...input} as="textarea" rows="3" placeholder={label} className={s.formControlInput} />
                {touched && error && (
                    <span className={cx(s.errorMessage, 'errorMessageRTL')}>{formatMessage(error)}</span>
                )}
            </Form.Group>
        );
    }

    renderSelectField = ({ input, label, meta: { touched, error }, children }) => {
        const { formatMessage } = this.props.intl;
        return (
            <Form.Group className={cx(s.space5, 'positionRelative')}>
                <Form.Label className={'inputLabel'}>{label}</Form.Label>
                <Form.Control as="select" {...input} className={s.formControlInput}>
                    {children}
                </Form.Control>
                {touched && error && (
                    <span className={cx(s.errorMessage, 'errorMessageRTL')}>{formatMessage(error)}</span>
                )}
            </Form.Group>
        );
    }

    render() {
        const { handleSubmit, submitting } = this.props;
        const { formatMessage } = this.props.intl;
        const { polygonPath, mapCenter } = this.state;

        return (
            <div className={s.root}>
                <Container fluid>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col lg={12} md={12} sm={12} xs={12}>
                                <div className={s.blackCenterSection}>
                                    <Row>
                                        <Col lg={6} md={12} sm={12} xs={12}>
                                            <Field
                                                name="locationName"
                                                type="text"
                                                component={this.renderField}
                                                label={formatMessage(messages.locationNameLabel)}
                                                placeholder={formatMessage(messages.locationNameLabel)}
                                            />
                                        </Col>
                                        <Col lg={6} md={12} sm={12} xs={12}>
                                            <Field
                                                name="isActive"
                                                component={this.renderSelectField}
                                                label={formatMessage(messages.locationIsActive)}
                                            >
                                                <option value="true">{formatMessage(messages.active)}</option>
                                                <option value="false">{formatMessage(messages.inactive)}</option>
                                            </Field>
                                        </Col>
                                        <Col lg={6} md={12} sm={12} xs={12}>
                                            <Field
                                                name="restrictDropOff"
                                                component={this.renderSelectField}
                                                label={formatMessage(messages.locationRestrictDropOff)}
                                            >
                                                <option value="false">{formatMessage(messages.inactive)}</option>
                                                <option value="true">{formatMessage(messages.active)}</option>
                                            </Field>
                                            <p className={s.helperText}>
                                                <FormattedMessage {...messages.locationRestrictDropOffHelper} />
                                            </p>
                                        </Col>
                                        <Col lg={12} md={12} sm={12} xs={12}>
                                            <Field
                                                name="description"
                                                component={this.renderTextArea}
                                                label={formatMessage(messages.locationDescription)}
                                            />
                                        </Col>
                                        <Col lg={12} md={12} sm={12} xs={12}>
                                            <Form.Group className={s.space5}>
                                                <Form.Label className={'inputLabel'}>
                                                    <FormattedMessage {...messages.locationCoordinates} />
                                                </Form.Label>
                                                <p className={s.helperText}>
                                                    <FormattedMessage {...messages.locationGeofencingHelper} />
                                                </p>
                                                <div className={s.mapActions}>
                                                    <Button
                                                        type="button"
                                                        onClick={this.handleClearPolygon}
                                                        className={cx(s.btnSecondary, s.btnSmall)}
                                                        disabled={polygonPath.length === 0}
                                                    >
                                                        <FormattedMessage {...messages.clearPolygon} />
                                                    </Button>
                                                    {polygonPath.length > 0 && (
                                                        <span className={s.pointsInfo}>
                                                            {polygonPath.length} {polygonPath.length === 1 ? 'point' : 'points'} placed
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={s.mapContainer}>
                                                    <GoogleMapsLoader>
                                                    <GoogleMap
                                                        mapContainerStyle={mapContainerStyle}
                                                        center={mapCenter}
                                                        zoom={12}
                                                        onClick={this.handleMapClick}
                                                    >
                                                        {polygonPath.map((point, i) => (
                                                            <OverlayView
                                                                key={`${point.lat}-${point.lng}-${i}`}
                                                                position={point}
                                                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                                            >
                                                                <div className={s.mapMarker}>{i + 1}</div>
                                                            </OverlayView>
                                                        ))}
                                                        {polygonPath.length >= 3 && (
                                                            <Polygon
                                                                paths={polygonPath}
                                                                options={{
                                                                    fillColor: '#2196F3',
                                                                    fillOpacity: 0.2,
                                                                    strokeColor: '#2196F3',
                                                                    strokeOpacity: 0.8,
                                                                    strokeWeight: 2,
                                                                }}
                                                            />
                                                        )}
                                                    </GoogleMap>
                                                    </GoogleMapsLoader>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg={12} md={12} sm={12} xs={12} className={s.spaceTop3}>
                                            <Button
                                                type="submit"
                                                disabled={submitting}
                                                className={cx(s.button, s.btnPrimary)}
                                            >
                                                <FormattedMessage {...messages.save} />
                                            </Button>
                                            <Link to="/siteadmin/location" className={cx(s.button, s.btnSecondary)}>
                                                <FormattedMessage {...messages.goBack} />
                                            </Link>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            </div>
        );
    }
}

const LocationFormWithRedux = reduxForm({
    form: 'LocationForm',
    validate,
    onSubmit: async (values, dispatch, props) => {
        // The select fields hand back strings
        const isActive = values.isActive === 'true' || values.isActive === true;
        const restrictDropOff = values.restrictDropOff === 'true' || values.restrictDropOff === true;
        await dispatch(props.manageLocation(
            values.locationName,
            values.coordinates,
            values.description,
            isActive,
            props.id,
            restrictDropOff
        ));
    }
})(LocationFormComponent);

const mapStateToProps = () => ({});
const mapDispatchToProps = { manageLocation };

export default withStyles(s)(injectIntl(connect(mapStateToProps, mapDispatchToProps)(LocationFormWithRedux)));
